/**
 * The bookkeeping behind a field that saves itself.
 *
 * Pure and separate from the component for two reasons. The repo has no jsdom
 * and no testing-library, so anything inside a component is untestable here. And
 * the rules that matter — never save two requests at once, never lose the last
 * sentence, never overwrite the other partner — are rules about state, not about
 * the DOM.
 *
 * The venue PATCH is optimistically concurrent: it takes the `updatedAt` you
 * last saw and returns 409 if the row moved underneath you. Autosave has to
 * respect that without turning every save into a question.
 */

export type AutosaveStatus =
  | { kind: 'idle' }
  | { kind: 'dirty' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error' }
  /** The other partner edited the same notes. Their text is held, not applied. */
  | { kind: 'conflict'; theirs: string };

export interface AutosaveState {
  /** What the person has typed. Always wins over anything arriving from the server. */
  text: string;
  /** The text the last successful save wrote, and what `theirs` is compared against. */
  saved: string;
  /** The `updatedAt` to send as the concurrency token. */
  baseUpdatedAt: string;
  /**
   * What to show. Deliberately *not* how in-flight is tracked: typing moves this
   * to `dirty` while a request is still out, and reading the flight from here
   * would let a second concurrent PATCH go out against a stale token.
   */
  status: AutosaveStatus;
  /** A request is out right now. */
  inFlight: boolean;
  /** Typing happened while that request was out, so run once more when it lands. */
  queued: boolean;
}

export const initialAutosave = (text: string, updatedAt: string): AutosaveState => ({
  text,
  saved: text,
  baseUpdatedAt: updatedAt,
  status: { kind: 'idle' },
  inFlight: false,
  queued: false,
});

export function onType(state: AutosaveState, text: string): AutosaveState {
  if (text === state.text) return state;
  return {
    ...state,
    text,
    status: { kind: 'dirty' },
    // A save already in flight cannot carry this keystroke, so remember to run
    // once more when it lands rather than firing a second request now.
    queued: state.inFlight,
  };
}

/** Whether a save should go out now. */
export const shouldSave = (state: AutosaveState): boolean =>
  !state.inFlight && state.status.kind !== 'conflict' && state.text !== state.saved;

export function onSaveStart(state: AutosaveState): AutosaveState {
  return { ...state, status: { kind: 'saving' }, inFlight: true, queued: false };
}

export function onSaved(state: AutosaveState, wrote: string, updatedAt: string): AutosaveState {
  return {
    ...state,
    saved: wrote,
    baseUpdatedAt: updatedAt,
    // Typing continued while the request was out, so this is not settled yet.
    status: state.text === wrote ? { kind: 'saved' } : { kind: 'dirty' },
    inFlight: false,
    queued: false,
  };
}

export function onError(state: AutosaveState): AutosaveState {
  return { ...state, status: { kind: 'error' }, inFlight: false, queued: false };
}

/**
 * A 409 came back. `theirs` is the notes text now on the server.
 *
 * Two different situations arrive here and only one is worth interrupting for:
 *
 * - The server's notes are what we last saved, so the conflict is about some
 *   *other* field — the partner changed the status, or the tour date. Our text
 *   is still the newest version of the notes, so retry against the fresh token.
 *   Asking about this would be noise.
 * - The server's notes differ, so the partner typed here too. **Never merge two
 *   people's prose.** Hold their version and let the person choose; neither text
 *   is thrown away in the meantime.
 */
export function onConflict(state: AutosaveState, theirs: string, updatedAt: string): AutosaveState {
  const settled = { ...state, baseUpdatedAt: updatedAt, inFlight: false, queued: false };
  if (theirs === state.saved) return { ...settled, status: { kind: 'dirty' } };
  if (theirs === state.text) {
    // They typed what we did. Nothing to resolve.
    return { ...settled, saved: theirs, status: { kind: 'saved' } };
  }
  return { ...settled, status: { kind: 'conflict', theirs } };
}

/** Keep what I wrote, over theirs. Saves next tick against the newer token. */
export function keepMine(state: AutosaveState): AutosaveState {
  return { ...state, status: { kind: 'dirty' } };
}

/** Take their version. Mine stays reachable in the UI until the drawer closes. */
export function takeTheirs(state: AutosaveState): AutosaveState {
  const theirs = state.status.kind === 'conflict' ? state.status.theirs : state.text;
  return { ...state, text: theirs, saved: theirs, status: { kind: 'saved' } };
}

/**
 * Server data arrived while nothing is being typed.
 *
 * Only adopted when there is nothing unsaved, because a background refetch has
 * no business replacing a half-written sentence — which is exactly what the
 * drawer's remount-on-every-save used to do.
 */
export function onRemote(state: AutosaveState, text: string, updatedAt: string): AutosaveState {
  /*
   * **Older than what we last wrote means stale, and stale is ignored.**
   *
   * This is the bug that ate a letter at a time. A save finishes, the venues
   * query is invalidated, and the refetch is a DynamoDB Query — which is
   * eventually consistent by default and can hand back the item from before the
   * write. The old text then did not match ours, so the last line of this
   * function adopted it and wiped the character that had just been typed. Type
   * slowly enough that each letter gets its own save, and every letter vanishes.
   *
   * The guard it was missing is time, not text. We know the token our own last
   * write produced; anything not newer than it cannot have anything to teach us,
   * whether it is a stale read or the echo of our own save. ISO instants compare
   * correctly as strings, which is the one thing this format is good for.
   */
  if (updatedAt <= state.baseUpdatedAt) return state;

  // Past the guard the token is strictly newer, so it is always worth taking —
  // it is what the next save has to be sent against. Only the text is in
  // question from here.
  if (state.text !== state.saved || state.inFlight) {
    // Something unsaved is on screen. Take the token, leave the prose alone: a
    // background refetch has no business replacing a half-written sentence.
    return { ...state, baseUpdatedAt: updatedAt };
  }
  if (text === state.saved) return { ...state, baseUpdatedAt: updatedAt };
  return { ...state, text, saved: text, baseUpdatedAt: updatedAt, status: { kind: 'idle' } };
}
